import { IBundleBuilderItem, IBundleBuilderSlabState } from '@/helpers/bundle-builder-storage';
import { BundleComparisonMode, BundleItemRole, IBundleProductFilterState, IBundleSlab, IBundleSlabGroup } from '@/interfaces/bundlePromotion';
import { IAddToCartItem, ICartItems } from '@/interfaces/cart';
import { IProductDetails } from '@/interfaces/product';

export interface IBundleTotalsItem {
  unitPrice: number;
  quantity: number;
  roles: Set<BundleItemRole>;
}

export interface IBundleGroupTotals {
  primaryQuantity: number;
  primaryAmount: number;
  secondaryQuantity: number;
  secondaryAmount: number;
}

export interface IBundleRequirementResult {
  fulfilled: boolean;
  remaining: {
    primaryQuantity?: number;
    primaryAmount?: number;
    secondaryQuantity?: number;
    secondaryAmount?: number;
  };
  // Populated only under an "Exact" comparison mode: how much to remove because the customer went
  // over the requirement (an "AtLeast" slab never disqualifies on overage, so this stays empty).
  excess: {
    primaryQuantity?: number;
    primaryAmount?: number;
    secondaryQuantity?: number;
    secondaryAmount?: number;
  };
}

export const computeGroupTotals = (items: IBundleTotalsItem[], scope: IBundleSlab['Scope']): IBundleGroupTotals => {
  const checkPrimary = scope === 'PrimaryOnly' || scope === 'Both';
  const checkSecondary = scope === 'SecondaryOnly' || scope === 'Both';

  let primaryQuantity = 0;
  let primaryAmount = 0;
  let secondaryQuantity = 0;
  let secondaryAmount = 0;

  items.forEach((item) => {
    const isPrimary = item.roles.has('Primary') || item.roles.has('PrimaryAndSecondary');
    const isSecondary = item.roles.has('Secondary') || item.roles.has('PrimaryAndSecondary');

    if (checkPrimary && isPrimary) {
      primaryQuantity += item.quantity;
      primaryAmount += item.unitPrice * item.quantity;
    }
    if (checkSecondary && isSecondary) {
      secondaryQuantity += item.quantity;
      secondaryAmount += item.unitPrice * item.quantity;
    }
  });
  return { primaryQuantity, primaryAmount, secondaryQuantity, secondaryAmount };
};

export const checkSlabRequirement = (slab: IBundleSlab, totals: IBundleGroupTotals): IBundleRequirementResult => {
  if (slab.RequirementMode === 'None') return { fulfilled: true, remaining: {}, excess: {} };

  const needsQuantity = slab.RequirementMode === 'Quantity' || slab.RequirementMode === 'Both';
  const needsAmount = slab.RequirementMode === 'Amount' || slab.RequirementMode === 'Both';
  const checkPrimary = slab.Scope === 'PrimaryOnly' || slab.Scope === 'Both';
  const checkSecondary = slab.Scope === 'SecondaryOnly' || slab.Scope === 'Both';

  const remaining: IBundleRequirementResult['remaining'] = {};
  const excess: IBundleRequirementResult['excess'] = {};
  let fulfilled = true;

  const evaluate = (actual: number, required: number, mode: BundleComparisonMode | undefined, key: keyof IBundleRequirementResult['remaining']) => {
    if (actual < required) {
      fulfilled = false;
      remaining[key] = required - actual;
    } else if (mode === 'Exact' && actual > required) {
      fulfilled = false;
      excess[key] = actual - required;
    }
  };

  if (checkPrimary) {
    if (needsQuantity && slab.PrimaryQuantity != null) {
      evaluate(totals.primaryQuantity, slab.PrimaryQuantity, slab.PrimaryComparisonMode, 'primaryQuantity');
    }
    if (needsAmount && slab.PrimaryAmount != null) {
      evaluate(totals.primaryAmount, slab.PrimaryAmount, slab.PrimaryComparisonMode, 'primaryAmount');
    }
  }
  if (checkSecondary) {
    if (needsQuantity && slab.SecondaryQuantity != null) {
      evaluate(totals.secondaryQuantity, slab.SecondaryQuantity, slab.SecondaryComparisonMode, 'secondaryQuantity');
    }
    if (needsAmount && slab.SecondaryAmount != null) {
      evaluate(totals.secondaryAmount, slab.SecondaryAmount, slab.SecondaryComparisonMode, 'secondaryAmount');
    }
  }

  return { fulfilled, remaining, excess };
};

// Uncapped per-unit percentage discount. MaxDiscountAmount caps the TOTAL discount for the whole
// bundle instance, not any single unit — capping here (before quantity and other eligible items
// are summed) would let the aggregate exceed the cap whenever a bundle has more than one eligible
// item or a quantity > 1. The cap is applied once, against the bundle-wide total, in
// calculateBundleDiscount below.
export const calculateSlabPerUnitDiscount = (slab: IBundleSlab, unitPrice: number): number => {
  if (slab.UsePercentage && slab.DiscountPercentage != null) {
    return unitPrice * (slab.DiscountPercentage / 100);
  }
  return 0;
};

export const itemGetsDiscount = (roles: Set<BundleItemRole>, discountTarget: IBundleSlab['DiscountTarget']): boolean => {
  if (discountTarget === 'Both') return true;
  if (discountTarget === 'Primary') return roles.has('Primary') || roles.has('PrimaryAndSecondary');
  if (discountTarget === 'Secondary') return roles.has('Secondary') || roles.has('PrimaryAndSecondary');
  return false;
};

// A percentage discount (UsePercentage: true) is summed per unit across every eligible item, then
// MaxDiscountAmount (if set) caps that BUNDLE-WIDE total once — not each item/unit individually,
// which would let a bundle with several eligible items blow past the intended cap (e.g. a 1000 cap
// with three items each individually under 1000 could otherwise total well over it). A flat
// DiscountAmount applies once to the whole bundle instance's total rather than once per item/unit
// (matching the API's cart/order-summary calculation), so it's added once here too. This only
// needs to return the bundle-wide total (used for the "Bundle Savings" preview) — there's no
// per-item discounted price shown anywhere in the frontend for bundles, so the capped total isn't
// split back across items here.
export const calculateBundleDiscount = (slab: IBundleSlab, items: IBundleTotalsItem[]): number => {
  const eligibleItems = items.filter((item) => itemGetsDiscount(item.roles, slab.DiscountTarget));
  if (eligibleItems.length === 0) return 0;

  if (slab.UsePercentage) {
    const totalDiscount = eligibleItems.reduce((sum, item) => sum + calculateSlabPerUnitDiscount(slab, item.unitPrice) * item.quantity, 0);
    const cappedTotal = slab.MaxDiscountAmount != null ? Math.min(totalDiscount, slab.MaxDiscountAmount) : totalDiscount;
    return Math.round(cappedTotal * 100) / 100;
  }

  return Math.round((slab.DiscountAmount ?? 0) * 100) / 100;
};

export const formatSlabRequirement = (slab: IBundleSlab): string => {
  if (slab.RequirementMode === 'None') return 'No purchase requirement';

  const needsQuantity = slab.RequirementMode === 'Quantity' || slab.RequirementMode === 'Both';
  const needsAmount = slab.RequirementMode === 'Amount' || slab.RequirementMode === 'Both';
  const checkPrimary = slab.Scope === 'PrimaryOnly' || slab.Scope === 'Both';
  const checkSecondary = slab.Scope === 'SecondaryOnly' || slab.Scope === 'Both';

  const parts: string[] = [];
  const exactly = (mode: BundleComparisonMode | undefined) => (mode === 'Exact' ? 'exactly ' : '');

  if (checkPrimary) {
    if (needsQuantity && slab.PrimaryQuantity != null)
      parts.push(`Buy ${exactly(slab.PrimaryComparisonMode)}${slab.PrimaryQuantity} Product${slab.PrimaryQuantity > 1 ? 's' : ''}`);
    if (needsAmount && slab.PrimaryAmount != null) parts.push(`Spend ৳${slab.PrimaryAmount}`);
  }
  if (checkSecondary) {
    if (needsQuantity && slab.SecondaryQuantity != null) parts.push(`+ ${exactly(slab.SecondaryComparisonMode)}${slab.SecondaryQuantity} more`);
    if (needsAmount && slab.SecondaryAmount != null) parts.push(`+ ৳${slab.SecondaryAmount} more`);
  }

  return parts.join(' ') || 'Add qualifying products';
};

export const formatSlabReward = (slab: IBundleSlab): string => {
  if (slab.UsePercentage && slab.DiscountPercentage != null) {
    const cap = slab.MaxDiscountAmount != null ? ` (up to ৳${slab.MaxDiscountAmount})` : '';
    return `Get ${slab.DiscountPercentage}% off${cap}`;
  }
  if (!slab.UsePercentage && slab.DiscountAmount != null) {
    return `Get ৳${slab.DiscountAmount} off`;
  }
  return '';
};

export const getGroupRequiredQuantity = (slab: IBundleSlab, group: IBundleSlabGroup): number | null | undefined => {
  if (group.Role === 'Secondary') return slab.SecondaryQuantity;
  return slab.PrimaryQuantity;
};

export const getGroupComparisonMode = (slab: IBundleSlab, group: IBundleSlabGroup): BundleComparisonMode | undefined => {
  if (group.Role === 'Secondary') return slab.SecondaryComparisonMode;
  return slab.PrimaryComparisonMode;
};

export const formatHeroTierLabel = (slab: IBundleSlab): { requirement: string; reward: string } => {
  const qty = slab.PrimaryQuantity ?? slab.SecondaryQuantity;
  const requirement = qty ? `Buy ${qty} Items` : 'Buy More';
  const reward =
    slab.UsePercentage && slab.DiscountPercentage != null
      ? `${slab.DiscountPercentage}% Off`
      : slab.DiscountAmount != null
        ? `৳${slab.DiscountAmount} Off`
        : '';
  return { requirement, reward };
};

export const groupCartItemsByBundle = (items: ICartItems[]): { bundleGroups: Map<string, ICartItems[]>; ungroupedItems: ICartItems[] } => {
  const bundleGroups = new Map<string, ICartItems[]>();
  const ungroupedItems: ICartItems[] = [];

  items.forEach((item) => {
    const bundleId = item.CartBundleInfoDto?.BundleId;
    if (!bundleId) {
      ungroupedItems.push(item);
      return;
    }
    const existing = bundleGroups.get(bundleId);
    if (existing) {
      existing.push(item);
    } else {
      bundleGroups.set(bundleId, [item]);
    }
  });

  return { bundleGroups, ungroupedItems };
};

export const EMPTY_BUNDLE_PRODUCT_FILTER: IBundleProductFilterState = {
  brand: [],
  gender: [],
  size: [],
  color: [],
  price: null,
};

export interface IBundleBuilderTotalsItem extends IBundleTotalsItem {
  item: IBundleBuilderItem;
  groupId: string;
}

// Every picked item for the slab, tagged with its group's role (primary/secondary) for totals and discounts.
export const flattenBuilderItems = (slab: IBundleSlab | undefined, state: IBundleBuilderSlabState | null): IBundleBuilderTotalsItem[] => {
  if (!slab || !state) return [];
  return (slab.Groups ?? []).flatMap((group) =>
    (state.itemsByGroupId[group.Id] ?? []).map((item) => ({
      unitPrice: item.unitPrice,
      quantity: item.quantity,
      roles: new Set<BundleItemRole>([group.Role]),
      item,
      groupId: group.Id,
    })),
  );
};

// The first picked item is the cart "trigger"; the whole build travels in addtoCartBundleInfo.
export const buildAddBundleToCartPayload = (slab: IBundleSlab, items: IBundleBuilderTotalsItem[]): IAddToCartItem => {
  const triggerItem = items[0].item;
  return {
    productId: triggerItem.productId,
    quantity: triggerItem.quantity,
    attributes: triggerItem.attributes,
    addtoCartBundleInfo: {
      bundlePromotionId: slab.BundlePromotionId,
      slabId: slab.Id,
      items: items.map(({ item, groupId }) => ({
        productId: item.productId,
        quantity: item.quantity,
        attributes: item.attributes,
        itemRole: slab.Groups?.find((group) => group.Id === groupId)?.Role ?? 'Primary',
      })),
    },
  };
};

// Resolves the picker's group; requirements are per group (Primary and Secondary can differ).
export const resolveBundleGroup = (slabs: IBundleSlab[] | undefined, slabId: string, groupId: string) => {
  const slab = slabs?.find((item) => item.Id === slabId);
  const group = slab?.Groups?.find((item) => item.Id === groupId);
  return {
    slab,
    group,
    groupIndex: slab?.Groups?.findIndex((item) => item.Id === groupId) ?? -1,
    totalGroups: slab?.Groups?.length ?? 0,
    requiredQuantity: slab && group ? getGroupRequiredQuantity(slab, group) : null,
    isExactRequirement: slab && group ? getGroupComparisonMode(slab, group) === 'Exact' : false,
  };
};

// Group category/brand scope plus the customer's refinements. A brand pick narrows within the group's brands.
const buildGroupScopeFilters = (group: IBundleSlabGroup, selection: IBundleProductFilterState): Record<string, string[]> => {
  const filterObject: Record<string, string[]> = {};
  const brands = selection.brand.length ? selection.brand : group.Brands;
  if (group.Categories?.length) filterObject.categories = group.Categories;
  if (brands?.length) filterObject.brandSeName = brands;
  if (selection.gender.length) filterObject.gender = selection.gender;
  if (selection.size.length) filterObject.sizes = selection.size;
  if (selection.color.length) filterObject.productColor = selection.color;
  if (selection.price) filterObject.price = [`${selection.price.min} TO ${selection.price.max}`];
  return filterObject;
};

// Search `filters` JSON for a group. Key order is part of the react-query key (and server seed), so keep it
// stable. SKU groups skip category/brand/refinement filters; storeCodes apply to every group.
export const buildBundleProductFilters = (
  group: IBundleSlabGroup | undefined,
  selection: IBundleProductFilterState,
  storeCodes: string[],
): string => {
  const filterObject: Record<string, string[]> = group && !group.Skus?.length ? buildGroupScopeFilters(group, selection) : {};
  if (storeCodes.length) filterObject.storeCodes = storeCodes;
  return Object.keys(filterObject).length ? JSON.stringify(filterObject) : '';
};

// Query + filters for a group's eligible-product search. Shared by the picker hook and the server prefetch
// so the react-query key (and seeded cache entry) cannot drift.
export const getBundleGroupSearchArgs = (
  group: IBundleSlabGroup | undefined,
  selection: IBundleProductFilterState = EMPTY_BUNDLE_PRODUCT_FILTER,
  storeCodes: string[] = [],
): { query: string; filters: string } => ({
  query: group?.Skus?.length ? group.Skus.join(' ') : '',
  filters: buildBundleProductFilters(group, selection, storeCodes),
});

const normalize = (value: unknown): string => (typeof value === 'string' ? value.trim().toLowerCase() : '');
const normalizeAll = (values: unknown[] | undefined | null): string[] => (values ?? []).map(normalize).filter(Boolean);

// Whether a PDP product belongs to a bundle group. A SKU group matches the style SKU or any variant SKU;
// otherwise categories and brands are ANDed (same as the picker's search filters). A group with no criteria matches nothing.
export const productMatchesGroup = (product: IProductDetails, group: IBundleSlabGroup): boolean => {
  const groupSkus = normalizeAll(group.Skus);
  if (groupSkus.length) {
    const productSkus = new Set(normalizeAll([product.Sku, ...(product.Variations ?? []).map((variation) => variation?.VariantSku)]));
    return groupSkus.some((sku) => productSkus.has(sku));
  }

  const groupCategories = normalizeAll(group.Categories);
  const groupBrands = normalizeAll(group.Brands);
  if (!groupCategories.length && !groupBrands.length) return false;

  const productCategories = new Set(
    normalizeAll([
      ...(product.ProductCategories ?? []).map((category) => category?.Slug),
      ...(product.Breadcrumbs ?? []).filter((crumb) => crumb?.IsCategory).map((crumb) => crumb.SeName),
    ]),
  );
  const categoryOk = !groupCategories.length || groupCategories.some((category) => productCategories.has(category));
  const brandOk = !groupBrands.length || groupBrands.includes(normalize(product.ProductBrand?.SeName));
  return categoryOk && brandOk;
};

// Group a PDP product should be seeded into: the first matching group, else the slab's first group.
export const findSeedGroup = (slab: IBundleSlab, product: IProductDetails): IBundleSlabGroup | undefined =>
  slab.Groups?.find((group) => productMatchesGroup(product, group)) ?? slab.Groups?.[0];
